/**
 * Web chat tool-card for the memory READ tools (M5.2):
 *
 * - recallMemory → nearest remembered nodes / facts / episodes for a query.
 * - addMemory    → confirmation of what was remembered (episode content).
 *
 * Web-only (a `.native.tsx` sibling renders iOS/Android). Copy from
 * `settings.memoryGraph.card.*`. Reads are defensive: the backend result shape
 * is probed across a few field names.
 */
import React, { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { MaterialSymbol } from '../../icons/MaterialSymbol';
import { CardShell } from './card-shell';
import { asArray, safeArray, safeGet } from './types';
import type { ToolCardProps } from './types';

const MAX_DISPLAY = 6;

interface RecallItem {
  primary: string;
  secondary?: string;
}

function extractRecallItems(result: unknown): RecallItem[] {
  const items: RecallItem[] = [];
  // hits: [{ node, distance }]
  for (const hit of safeArray<Record<string, unknown>>(result, 'hits')) {
    const node = safeGet<Record<string, unknown>>(hit, 'node') ?? hit;
    const label = safeGet<string>(node, 'label') || safeGet<string>(node, 'summary');
    if (label) {
      items.push({
        primary: label,
        secondary:
          safeGet<string>(node, 'sublabel') ||
          safeGet<string>(node, 'nodeType') ||
          undefined,
      });
    }
  }
  for (const fact of safeArray<Record<string, unknown>>(result, 'facts')) {
    const summary = safeGet<string>(fact, 'summary');
    if (summary) items.push({ primary: summary, secondary: safeGet<string>(fact, 'kind') });
  }
  for (const ep of safeArray<Record<string, unknown>>(result, 'episodes')) {
    const content = safeGet<string>(ep, 'content');
    if (content) items.push({ primary: content, secondary: safeGet<string>(ep, 'kind') });
  }
  for (const node of safeArray<Record<string, unknown>>(result, 'relatedNodes')) {
    const label = safeGet<string>(node, 'label');
    if (label) items.push({ primary: label, secondary: safeGet<string>(node, 'nodeType') });
  }
  if (items.length === 0) {
    for (const node of asArray<Record<string, unknown>>(result)) {
      const label = safeGet<string>(node, 'label') || safeGet<string>(node, 'summary');
      if (label) items.push({ primary: label });
    }
  }
  return items;
}

export const MemoryCard: FC<ToolCardProps> = ({ toolName, args, result }) => {
  const { t } = useTranslation('common');
  const isAdd = toolName === 'addMemory';

  if (isAdd) {
    const content =
      safeGet<string>(result, 'content') ||
      safeGet<string>(args, 'content') ||
      safeGet<string>(result, 'summary');
    return (
      <CardShell>
        <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <MaterialSymbol name="bookmark_added" size={16} style={{ color: 'var(--success, #1a7f37)' }} />
            <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-tertiary)' }}>
              {t('settings.memoryGraph.card.remembered')}
            </span>
          </div>
          {content ? (
            <div
              style={{
                fontSize: 14,
                lineHeight: '20px',
                color: 'var(--text-primary)',
                display: '-webkit-box',
                WebkitLineClamp: 4,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
              dir="auto"
            >
              {content}
            </div>
          ) : null}
        </div>
      </CardShell>
    );
  }

  const all = extractRecallItems(result);
  const items = all.slice(0, MAX_DISPLAY);
  const remaining = all.length - items.length;

  return (
    <CardShell>
      <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <MaterialSymbol name="network_node" size={15} style={{ color: 'var(--text-tertiary)' }} />
          <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-tertiary)' }}>
            {t('settings.memoryGraph.card.recalled')}
          </span>
        </div>

        {items.length === 0 ? (
          <span style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>
            {t('settings.memoryGraph.card.recallEmpty')}
          </span>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {items.map((item, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 3,
                  padding: '10px 0',
                  borderTop: index > 0 ? '1px solid var(--input-border)' : 'none',
                }}
              >
                <span
                  style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)' }}
                  dir="auto"
                >
                  {item.primary}
                </span>
                {item.secondary ? (
                  <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }} dir="auto">
                    {item.secondary}
                  </span>
                ) : null}
              </div>
            ))}
          </div>
        )}

        {remaining > 0 ? (
          <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
            {t('settings.memoryGraph.card.more', { count: remaining })}
          </span>
        ) : null}
      </div>
    </CardShell>
  );
};
